<template>
  <checkout-step
    title="ecommerce.checkout.shipping.stepTitle"
    :completion-condition-text="completionConditionText"
    :complete="stepData.isComplete && ! isForceEdit"
    @checkout-step:request-edit="forceEdit">
    <template #default>
      <div class="relative">

        <spinner :show="isLoading" overlay="absolute" size="4" />

        <h6 class="checkout-step-subtitle">
          {{
            translateUcFirst("ecommerce.checkout.shipping.shippingMethodTitle")
          }}
        </h6>
        <large-options-input
          v-model="orderData.shipping.method"
          :show-price="true"
          :option-component="'LargeOptionAddOn'"
          :list="shippingMethodFieldOptions"></large-options-input>

        <div class="address-select margin-xl-top animate__fadeIn animate--faster" v-if="addressRequired">
          <h6 class="checkout-step-subtitle margin-remove">
            {{ translateUcFirst("ecommerce.checkout.shipping.addressTitle") }}
          </h6>
          <p
            class="animate__fadeIn animate--faster"
            v-if="showOnDemandAddressUi && showSelectAddressUi">
            {{
              translateUcFirst(
                "ecommerce.checkout.shipping.addressFillOptionsExplain"
              )
            }}
          </p>

          <div class="flex gap-m flex-wrap l:flex-nowrap flex-bottom width-expand margin-l-top" v-if="showSelectAddressUi">
            <form-input
              v-if="addressListForSelectInput?.length > 0"
              v-model="orderData.shipping.addressId"
              type="select2"
              icon="map-pin-location"
              :error-message="addressSelectErrorMessage"
              error-display-position="absolute"
              class="width-expand"
              :list="addressListForSelectInput"
              :translate-list="false"
              :label="false"
              placeholder="ecommerce.checkout.shipping.selectAddressInputPlaceholder" />

            <form-button
              style="min-width: 150px"
              icon="plus"
              theme="alt"
              text="ecommerce.checkout.shipping.addAddressCta"
              @click="isCreatingAddress = true"></form-button>
          </div>

          <div
            class="relative padding margin-m-block margin-auto-inline max-width-300 animate__fadeIn animate--faster"
            v-if="showOnDemandAddressUi && showSelectAddressUi">
            <span
              class="inline-block padding-inline padding-s-block absolute-center text-l bold bg-bg">
              {{ translate("ecommerce.checkout.shipping.or") }}
            </span>
            <hr class="" />
          </div>
          <div
            class="animate__fadeIn animate--faster margin-s-top"
            v-if="showOnDemandAddressUi">
            <base-form
              :auto-set-default-values="true"
              v-model="orderData.shipping.address"
              ref="onDemandAddressForm"
              @form:formBecameValid="handleOnDemandAddressFormBecomingValid"
              @form:formBecameInvalid="setOnDemandAddressAsInvalid"
              :config="computedCreateAddressFormConfig"></base-form>
          </div>
        </div>
        <div class="shipping-component margin-xl-top" v-if="shippingComponent">
          <component
            :shipping-component-hint="shippingComponentHint"
            @update:shipping-component-hint="shippingComponentHint = $event"
            v-bind="{ stepData, orderData, cart, refreshQuote }"
            v-model="isShippingComponentSatisfied"
            :is="shippingComponent"></component>
        </div>

        <create-user-address-modal
          :opened="isCreatingAddress"
          @modal:closed="isCreatingAddress = false"
          @create-address:success="
            addressCreatedHandler
          "></create-user-address-modal>

        <div class="width-expand margin-l-top margin-s-bottom">
          <form-button class="width-expand" theme="lead-inverse" text="ecommerce.checkout.shipping.closeStep" v-if="isForceEdit && stepData.isComplete" @click="isForceEdit=false"></form-button>
        </div>
      </div>
    </template>
    <template #complete>
      <span class="text-l">
        <span class="text-s"
          >{{ translateUcFirst(currentShippingMethod.label) }} ({{
            getShippingMethodPriceString(currentShippingMethod)
          }})
        </span>
        <br />
        <span
          v-if="currentShippingMethod.requiresAddress"
          class="text-s text-light"
          >{{ selectedAddressString }}</span
        >
      </span>
    </template>
  </checkout-step>
</template>

<script>
import { computed } from "vue";
import asyncOperations from "@/client/extensions/composition/asyncOperations.js";
import useCarts from "@/client/extensions/composition/useCarts.js";
import CheckoutStepComponent from "@/client/components/ecommerce/checkout/CheckoutStep";
import CreateUserAddressModal from "@/client/components/ecommerce/address/CreateUserAddressModal.vue";
import asyncComponentFactory from "@/client/extensions/modules/asyncComponentFactory";
import checkoutStep from "@/client/extensions/mixins/checkoutStep.js";

export default {
  mixins: [checkoutStep],
  components: {
    CheckoutStep: CheckoutStepComponent,
    CreateUserAddressModal,
  },
  props: {
    stepData: {
      type: Object,
      default: () => {},
    },
    orderData: {
      type: Object,
      default: () => {},
    },
    cart: {
      type: Object,
      default: () => {},
    },
    refreshQuote: {
      type: Function,
      default: () => {},
    },
  },
  setup(props) {
    const { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations();
    let carts = useCarts(props);
    return { asyncOps, asyncOpsReady, asyncStatus, carts };
  },
  data () {
    return {
      stepLanguageKey: "shipping",
      shippingMethods: [],
      addresses: [],
      createAddressFormConfig: {},
      isCreatingAddress: false,
      isOnDemandAddressValid: false,
      asyncData: {
        shippingMethods: {
          target: computed(
            () => `ecommerce/shipping/methods/cart/${this.cart.uuid}`
          ),
          data: {},
          options: {
            dataMutator: (data, res) => {
              return data;
            },
          },
        },
        createAddressFormConfig: {
          target: `entity/address/create`,
        },
        addresses: {
          target: computed(() => `ecommerce/address/`),
          data: {
            userId: this.$store.getters["user/profile"].id,
          },
          options: {
            dataMutator: (data, res) => {
              if (data.items) {
                return data.items;
              }

              return [];
            },
          },
        },
      },
      addressSelectErrorMessage: "",
      addressRequired: false,
      allowOnDemandAddress: false,
      allowSelectAddress: false,
      shippingComponent: false,
      isLoading: false,
      isShippingComponentSatisfied: false,
      shippingComponentHint: false,
      isForceEdit: false,
    };
  },
  computed: {
    showSelectAddressUi() {
      return this.allowSelectAddress;
    },
    showOnDemandAddressUi() {
      if (!this.allowOnDemandAddress) {
        return false;
      }

      return !this.orderData.shipping.addressId;
    },
    computedCreateAddressFormConfig() {
      let base = { ...this.createAddressFormConfig };
      base.buttons = {};
      base.fields.OwnerId = {
        type: "hidden",
        name: "OwnerId",
        defaultValue: "DYNAMIC_currentUserId",
      };

      return base;
    },
    addressListForSelectInput() {
      let res = [];
      if (!this.addresses || !Array.isArray(this.addresses)) {
        return [];
      }
      this.addresses.forEach((address) => {
        res.push({
          value: address.id,
          label: address.addressString,
        });
      });

      return res;
    },
    shippingMethodFieldOptions() {
      if (!this.shippingMethods || !Array.isArray(this.shippingMethods)) {
        return [];
      }

      let res = this.shippingMethods.map((item) => ({
        value: item.type,
        label: item.label,
        title: item.label,
        text: item.description,
        price: item.price,
        currency: this.cart?.quote?.currency
          ? this?.cart?.quote?.currency
          : undefined,
        // selected: item.price === 0,
      }));

      return res;
    },
    currentShippingMethod() {
      if (!this.shippingMethods) {
        return false;
      }

      for (const method of this.shippingMethods) {
        if (method.type === this.orderData.shipping.method) {
          return method;
        }
      }
      return false;
    },
    canComplete() {
      if (!this.currentShippingMethod) {
        return false;
      }

      if (this.shippingComponent && !this.isShippingComponentSatisfied) {
        return false;
      }

      // support the one time address here
      // if need address and has selected address -> ok
      // if need address and can have temp address and that address is valid -> ok
      if (
        this.currentShippingMethod.requiresAddress &&
        this.orderData.shipping.addressId
      ) {
        return true;
      }
      if (
        this.currentShippingMethod.requiresAddress &&
        this.currentShippingMethod.allowOnDemandAddress &&
        this.isOnDemandAddressValid
      ) {
        return true;
      }

      return false;
    },
    selectedAddressString() {
      let addressId = this.orderData.shipping.addressId;
      if (!addressId) {
        return "";
      }

      for (const address of this.addresses) {
        if (address.id === addressId) {
          return address.addressString;
        }
      }

      return "";
    },
    completionConditionText() {
      if (this.canComplete) {
        return "";
      }

      let hints = [];
      if (!this.currentShippingMethod) {
        hints.push(
          this.t("ecommerce.checkout.shipping.selectShippingMethodHint")
        );
      }

      let hasValidOnDemandAddress =
        this.currentShippingMethod.requiresAddress &&
        this.currentShippingMethod.allowOnDemandAddress &&
        this.isOnDemandAddressValid;

      let hasValidSelectedAddress = this.orderData.shipping.addressId;

      if (
        this.currentShippingMethod.requiresAddress &&
        !(hasValidSelectedAddress || hasValidOnDemandAddress)
      ) {
        hints.push(this.t("ecommerce.checkout.shipping.addAddressHint"));
      }

      if (
        this.shippingComponent &&
        !this.isShippingComponentSatisfied &&
        this.shippingComponentHint
      ) {
        hints.push(this.shippingComponentHint);
      }

      return hints.join(", ") + ".";
    },
  },
  watch: {
    canComplete(newVal) {
      if (!newVal) {
        this.stepData.isComplete = false;
      } else {
        this.attemptToCompleteStep();
      }
    },
    shippingMethods(newVal) {
      if (newVal.length === 1) {
        this.orderData.shipping.method = newVal[0].type;
      }
    },
    "orderData.shipping.addressId": function (newVal) {
      this.orderData.shipping.addressId = newVal;
    },
    currentShippingMethod(newVal) {
      if (!this.currentShippingMethod.requiresAddress) {
        this.addressSelectErrorMessage = "";
        this.addressRequired = false;
      } else {
        this.addressRequired = true;
      }

      this.allowOnDemandAddress =
        this.currentShippingMethod.allowOnDemandAddress;

      this.allowSelectAddress = this.currentShippingMethod.allowSelectAddress;

      if ( !this.allowSelectAddress) {
        this.orderData.shipping.addressId = undefined;
      }
      this.orderData.shipping.method = newVal.type;
      this.setShippingComponent(newVal.type);
    },
    orderData: {
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.addressSelectErrorMessage = "";
        }
      },
    },
  },
  methods: {
    forceEdit() {
      this.isForceEdit = true;
    },
    async handleOnDemandAddressFormBecomingValid(e) {
      let preflightResult = await this.$refs.onDemandAddressForm.doPreflight();
      this.isOnDemandAddressValid = preflightResult === true;
    },
    setOnDemandAddressAsInvalid() {
      this.isOnDemandAddressValid = false;
    },
    async addressCreatedHandler(data) {
      this.isCreatingAddress = false;
      await this.refreshAsyncData();
      this.orderData.shipping.addressId = this.addresses[0].id;
    },
    attemptToCompleteStep(arg) {
      if (this.canComplete) {
        this.stepData.isComplete = true;
        this.refreshQuote();
      } else {
        this.stepData.isComplete = false;
        this.addressSelectErrorMessage = this.translate(
          "ecommerce.checkout.errorMessages.selectAnAddressBeforeConfirmAddress"
        );
      }
    },
    getShippingMethodPriceString(item) {
      if (item.price && item.price != 0) {
        return this.translateNumber(item.price, "currency", {
          currency: this.cart.quote.currency,
        });
      } else {
        return this.translate("ecommerce.general.free");
      }
    },
    async setShippingComponent(name) {
      this.isLoading = true;
      this.isShippingComponentSatisfied = false;
      let fullComponentName = utilities.ucFirst(name);
      let target = `components/ecommerce/checkout/shipping/${fullComponentName}.vue`;

      let component = asyncComponentFactory(target, false);

      // throws exception if none exists
      try {
        let finalShippingComponent = await component();
        if (finalShippingComponent) {
          this.shippingComponent = asyncComponentFactory(target, true);
        } else {
          this.shippingComponent = false;
        }
      } catch (e) {
        this.shippingComponent = false;
      }
      this.isLoading = false;
    },
  },
};
</script>

<style scoped lang="scss"></style>
